import { FILTER_TYPE_CHECKBOX, NOTIFICATION_EVENT_TYPE_PHRASES_GENERATED, NOTIFICATION_EVENT_TYPE_PHRASES_UPDATED_FAIL, NOTIFICATION_EVENT_TYPE_PREFERENCE_UPDATE_FAIL, SMALL_WEB_SCREEN_WIDTH, TABLET_WIDTH } from "../constant";
import { DEFAULT_TIMEZONE } from '../constant/timezones';
import i18n from "../i18nService";
import { getUserTimezone } from '../services/currentUserStore';
import { G_UNIT_NAME, MCG_UNIT_NAME, MG_UNIT_NAME } from "./productsTab/products/ProductPropertyNumberValue";
import {ProductCategory, FormGroup} from "../productTypes";

export const MODAL_SMALL_SIZE = 'small';
export const MODAL_SUPER_SMALL_SIZE = 'xs';
export { getUserTimezone };
export { DEFAULT_TIMEZONE };

export const isSmallWebScreen = (width) => {
    return width < SMALL_WEB_SCREEN_WIDTH && width > TABLET_WIDTH;
};

export const ADD_PRODUCT_MACRO_ELEMENTS = 'macronutrient';
export const ADD_PRODUCT_MICRO_ELEMENTS = 'micronutrient';
export const ADD_PRODUCT_PHYSICAL_PROPERTIES = 'physical';

export const MAIN_RECIPE_ELEMENTS = 'base';
export const SECONDARY_RECIPE_ELEMENTS = 'adds';
export const TASTE_RECIPE_PROPERTIES = 'tasty';
export const LIQUID_RECIPE_ELEMENTS = 'liquid';

export const sortByDate = (a, b) => {
    return new Date(b.dateCreated) - new Date(a.dateCreated);
};

const WEEKDAY_TO_INDEX = {
    Sun: 0,
    Mon: 1,
    Tue: 2,
    Wed: 3,
    Thu: 4,
    Fri: 5,
    Sat: 6,
};

const resolveTimezone = (timezone) => timezone || getUserTimezone() || DEFAULT_TIMEZONE;

export const getZonedParts = (date = Date.now(), timezone = getUserTimezone()) => {
    const tz = resolveTimezone(timezone);
    try {
        const parts = new Intl.DateTimeFormat('en-US', {
            timeZone: tz,
            year: 'numeric',
            month: 'numeric',
            day: 'numeric',
            weekday: 'short',
            hour: 'numeric',
            minute: 'numeric',
            second: 'numeric',
            hourCycle: 'h23',
        }).formatToParts(new Date(date));
        const map = Object.fromEntries(parts.filter((part) => part.type !== 'literal').map((part) => [part.type, part.value]));
        return {
            year: Number(map.year),
            monthIndex: Number(map.month) - 1,
            monthNumber: Number(map.month),
            dayNumber: Number(map.day),
            weekdayIndex: WEEKDAY_TO_INDEX[map.weekday] ?? 0,
            hour: Number(map.hour),
            minute: Number(map.minute),
            second: Number(map.second),
            timezone: tz,
        };
    } catch (err) {
        return getZonedParts(date, DEFAULT_TIMEZONE);
    }
};

export const zonedDateFromParts = (year, monthIndex, dayNumber, timezone = getUserTimezone()) => {
    const tz = resolveTimezone(timezone);
    let ms = Date.UTC(year, monthIndex, dayNumber, 12, 0, 0);
    for (let i = 0; i < 4; i += 1) {
        const parts = getZonedParts(ms, tz);
        const desired = Date.UTC(year, monthIndex, dayNumber);
        const actual = Date.UTC(parts.year, parts.monthIndex, parts.dayNumber);
        const deltaMs = desired - actual;
        if (deltaMs === 0) {
            break;
        }
        ms += deltaMs;
    }
    return new Date(ms);
};

export const parseDateInput = (value, timezone = getUserTimezone()) => {
    if (value == null || value === '') {
        return new Date();
    }
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
        const [year, month, day] = value.slice(0, 10).split('-').map(Number);
        return zonedDateFromParts(year, month - 1, day, timezone);
    }
    return new Date(value);
};

export const getDate = (date, timezone = getUserTimezone()) => {
    try {
        return new Intl.DateTimeFormat('en-CA', {
            timeZone: resolveTimezone(timezone),
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
        }).format(new Date(date));
    } catch (err) {
        return new Intl.DateTimeFormat('en-CA', {
            timeZone: DEFAULT_TIMEZONE,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
        }).format(new Date(date));
    }
};

export const checkItemLocaleCode = (itemCode) => itemCode !== null && itemCode !== undefined;

export const createPhrasesMessage = (type, preferenceAsString) => {
    let result = '';
    if(type === NOTIFICATION_EVENT_TYPE_PHRASES_GENERATED) {
        result = `${i18n.t('app.page.preference.phrasesForPreference')} «${preferenceAsString}» ${i18n.t('app.page.preference.generated')}`;
    }
    if(type === NOTIFICATION_EVENT_TYPE_PHRASES_UPDATED_FAIL) {
        result = `${i18n.t('app.page.preference.phrasesGenerateFail')} «${preferenceAsString}»`;
    }
    if(type === NOTIFICATION_EVENT_TYPE_PREFERENCE_UPDATE_FAIL) {
        result = `${i18n.t('app.page.preference.preferenceGenerateFail')} «${preferenceAsString}»`;
    }
    return result;
};

export const createCategoriesList = (withAny) => {
    const result = [];
    Object.values(ProductCategory).forEach(it => {
        result.push({id: it, name: i18n.t(`product.scheme.formGroup.${it}`)})
    });
    withAny ? result.push({id: 'any', name: i18n.t(`product.scheme.formGroup.any`)}) : null;
    return result;
};

export const createFormGroupsList = (withAny) => {
    const result = [];
    Object.values(FormGroup).forEach((it) => {
        if (it === FormGroup.ANY && !withAny) {
            return;
        }
        result.push({ id: it, name: i18n.t(`product.scheme.formGroup.${it}`) });
    });
    return result;
};

export const createFiltersListProductCategories = (withAny, filterGroupName) => {
    const result = [];
    Object.values(ProductCategory).forEach(it => result.push({
        filterTitle: i18n.t(`product.scheme.formGroup.${it}`),
        filterName: it,
        filterType: FILTER_TYPE_CHECKBOX,
        variants: [],
        value: false,
        filterGroupName: filterGroupName,
    }));
    if(withAny) {
        result.push({
            filterTitle: i18n.t('product.scheme.formGroup.any'),
            filterName: 'any',
            filterType: FILTER_TYPE_CHECKBOX,
            variants: [],
            value: false,
            filterGroupName: filterGroupName,
        });
    }
    return result;
};

export const createFiltersListFormGroups = (withAny, filterGroupName) => {
    const result = [];
    Object.values(FormGroup).forEach((it) => {
        if (it === FormGroup.ANY && !withAny) {
            return;
        }
        result.push({
            filterTitle: i18n.t(`product.scheme.formGroup.${it}`),
            filterName: it,
            filterType: FILTER_TYPE_CHECKBOX,
            variants: [],
            value: false,
            filterGroupName: filterGroupName,
        });
    });
    return result;
};

export const findLogicUnits = (value) => {
    if(+value > 1000000) {
        return G_UNIT_NAME;
    }
    if(+value > 1000) {
        return MG_UNIT_NAME;
    }
    return MCG_UNIT_NAME;
};
